import {
  Badge,
  Loader,
  Progress,
  Stack,
  Table,
  Tooltip,
  Whisper
} from 'rsuite';
import ProvisionService from './Provision';
import Instance from './Instance';
import { selectInstancesExtraInfo } from '@monorepo/shared/store/instanceExtraSlice';
import { CSSProperties, useEffect, useState } from 'react';
import { formatPercent } from '@monorepo/shared';
import { useAppSelector } from '@monorepo/shared/store/hook';
import {
  selectInstances,
  selectRejectedInstances
} from '@monorepo/shared/store/instanceSlice';
import { selectCurrentGroup } from '@monorepo/shared/store/groupSlice';
import { selectAssets } from '@monorepo/shared/store/assetSlice';
import { t } from 'i18next';
import { selectRejectedRegions } from '@monorepo/shared/store/authSlice';

const { Column, HeaderCell, Cell } = Table;
export const ExpandedRow = ({ rowData }: IRowProps) => {
  const instances = useAppSelector(selectInstances);
  const rejectedRegions = useAppSelector(selectRejectedRegions);
  const rejectedInstances = useAppSelector(selectRejectedInstances);
  const currentGroups = useAppSelector(selectCurrentGroup);
  const [isMaster, setIsMaster] = useState<boolean>(false);
  const [isProvisioned, setIsProvisioned] = useState<boolean>(false);
  const [isRejectedRegion, setIsRejectedRegion] = useState<boolean>(false);
  const [rejectedData, setRejectedData] = useState<Record<string, any>>({});
  const [hasInstance, setHasInstance] = useState<boolean>(false);
  const [contentLeftPadding, setContentLeftPadding] = useState<CSSProperties>(
    {}
  );
  const [instance, setInstance] = useState<Record<string, any>>({});
  useEffect(() => {
    const inst = instances?.[currentGroups.id]?.[rowData?.account_id];
    const rejectedData =
      rejectedInstances?.[currentGroups.id]?.[rowData?.account_id] || {};
    const rejectedRegionNames = Object.keys(rejectedRegions);
    setIsRejectedRegion(
      rejectedRegionNames.length > 0 &&
        rejectedRegionNames.includes(rowData?.region?.alias)
    );
    setRejectedData(rejectedData);
    setIsProvisioned(rowData?.region && rowData?.region?.url);
    setHasInstance(!!inst);
    if (!!inst) {
      setInstance(inst);
    }
    setIsMaster(rowData?.user_type === 'Master');
    setContentLeftPadding({ paddingLeft: rowData?.level * 30 + 5 + 'px' });
  }, [rowData, instances, currentGroups, rejectedRegions, rejectedInstances]);
  const getContent = () => {
    if (isProvisioned) {
      if (hasInstance) {
        if (instance?.task_id) {
          const taskObj = {
            task_id: instance.task_id,
            account_id: rowData?.account_id
          };
          return (
            <ProvisionService
              rowData={rowData || {}}
              existedTaskObj={taskObj}
            />
          );
        } else {
          return <Instance rowData={rowData || {}} />;
        }
      } else if (isRejectedRegion || rejectedData?.account) {
        return (
          <Stack justifyContent="center" style={{ height: '100%' }}>
            <p>{t('common.msgConst.instanceRegionRejected')}</p>
            {rejectedData?.error?.message && (
              <b>{`:[${rejectedData?.error?.message}]`}</b>
            )}
          </Stack>
        );
      } else {
        return (
          <Stack justifyContent="center" style={{ height: '100%' }}>
            <Loader content={t('common.loading')}></Loader>
          </Stack>
        );
      }
    } else {
      if (isMaster) {
        return <ProvisionService rowData={rowData || {}} />;
      } else {
        return (
          <Stack justifyContent="center" style={{ height: '100%' }}>
            {hasInstance ? (
              <p>{t('common.msgConst.instanceNotFound')}</p>
            ) : (
              <p>{t('common.msgConst.sub_user_provision_decline')}</p>
            )}
          </Stack>
        );
      }
    }
  };
  return (
    <div className="account-expand-content" style={contentLeftPadding}>
      {getContent()}
    </div>
  );
};
export const renderNormalRow = (ouLevel: number, isGroupView?: boolean) => {
  const ouWidth: number = ouLevel * 30 + 280;
  const getAccountRow = (
    rowData: Record<string, any>,
    keyName: string,
    style?: CSSProperties
  ) => {
    return (
      <span
        className={rowData?.account_id ? 'account-row' : ''}
        style={style}
        title={rowData?.[keyName]}
      >
        {rowData?.[keyName]}
      </span>
    );
  };
  const getColHeaderWithTooltip = (content: string) => {
    return (
      <Whisper
        placement="top"
        controlId="control-id-hover"
        trigger="hover"
        speaker={<Tooltip>{content}</Tooltip>}
      >
        <span>{content}</span>
      </Whisper>
    );
  };

  return (
    <>
      <Column
        width={ouWidth}
        minWidth={ouWidth}
        fixed
        sortable={isGroupView || ouLevel === 0}
      >
        <HeaderCell>
          {getColHeaderWithTooltip(t('fsa.organization.cols.ou'))}
        </HeaderCell>
        <Cell dataKey="account_company">
          {(rowData) => {
            const level = rowData?.level || 0;
            const width = ouWidth - level * 30 - 50;
            const icon = rowData?.account_id ? (
              <i
                className="icon-fontello-doc"
                style={{ fontSize: '20px', color: '#848baa' }}
              />
            ) : rowData?.rootNode ? (
              <i
                className="icon-fontello-rootfolder"
                style={{ fontSize: '22px', color: '#848baa' }}
              />
            ) : (
              <i
                className="icon-fontello-folder"
                style={{ fontSize: '20px', color: '#848baa' }}
              />
            );
            return (
              <div
                className={rowData?.account_id ? 'account-row' : ''}
                style={
                  rowData?.children
                    ? {
                        width: width,
                        overflow: 'hidden',
                        display: 'inline-flex'
                      }
                    : {
                        width: width,
                        overflow: 'hidden',
                        display: 'inline-flex',
                        paddingLeft: '26px'
                      }
                }
              >
                {icon}
                <div
                  style={{
                    display: 'flex'
                  }}
                >
                  <div
                    style={{
                      color: '#212121',
                      maxWidth: rowData?.account_id ? width - 80 : width,
                      display: 'inline-block',
                      overflow: 'hidden',
                      whiteSpace: 'nowrap',
                      textOverflow: 'ellipsis'
                    }}
                    title={rowData?.ou}
                  >
                    {rowData?.ou}
                  </div>
                  {getInfoIcons(rowData)}
                </div>
              </div>
            );
          }}
        </Cell>
      </Column>
      <Column
        width={170}
        minWidth={170}
        sortable={isGroupView || ouLevel === 0}
      >
        <HeaderCell>
          {getColHeaderWithTooltip(t('fsa.organization.cols.accountId'))}
        </HeaderCell>
        <Cell dataKey="account_id">
          {(rowData) => {
            const user_type =
              rowData?.user_type === 'Master'
                ? 'Primary'
                : rowData?.user_type === 'ThirdParty' && rowData?.idp_role_name
                ? rowData?.idp_role_name
                : rowData?.user_type;
            return (
              <Stack spacing={6}>
                <span className={rowData?.account_id ? 'account-row' : ''}>
                  <div style={{ display: 'inline-block', minWidth: '60px' }}>
                    {rowData?.account_id}
                  </div>
                </span>
                {rowData?.user_type && (
                  <Badge
                    style={{
                      fontSize: '0.8em',
                      fontWeight: 'bold',
                      textAlign: 'center',
                      padding: '2px 10px',
                      borderRadius: '10px',
                      backgroundColor:
                        rowData?.user_type === 'Master'
                          ? 'var(--light-blue)'
                          : 'var(--light-green)'
                    }}
                    content={user_type}
                  />
                )}
              </Stack>
            );
          }}
        </Cell>
      </Column>
      <Column
        width={160}
        minWidth={160}
        sortable={isGroupView || ouLevel === 0}
      >
        <HeaderCell>
          {getColHeaderWithTooltip(t('fsa.organization.cols.owner'))}
        </HeaderCell>
        <Cell dataKey="master_user_name">
          {(rowData) => getAccountRow(rowData, 'master_user_name')}
        </Cell>
      </Column>
      <Column
        width={160}
        minWidth={160}
        sortable={!isGroupView && ouLevel === 0}
      >
        <HeaderCell>
          {getColHeaderWithTooltip(t('fsa.organization.cols.serviceRegion'))}
        </HeaderCell>
        <Cell dataKey="regionName">
          {(rowData) => getAccountRow(rowData, 'regionName')}
        </Cell>
      </Column>

      <Column
        width={100}
        minWidth={100}
        sortable={!isGroupView && ouLevel === 0}
      >
        <HeaderCell>
          {getColHeaderWithTooltip(t('fsa.organization.cols.vCPU'))}
        </HeaderCell>
        <Cell dataKey="vCPU">
          {(rowData) => <InstanceCol rowData={rowData} name="vCPU" />}
        </Cell>
      </Column>
      <Column
        width={100}
        minWidth={100}
        sortable={!isGroupView && ouLevel === 0}
      >
        <HeaderCell>
          {getColHeaderWithTooltip(t('fsa.organization.cols.RAM'))}
        </HeaderCell>
        <Cell dataKey="ram">
          {(rowData) => <InstanceCol rowData={rowData} name="ram" />}
        </Cell>
      </Column>
      <Column
        width={160}
        minWidth={160}
        flexGrow={1}
        sortable={!isGroupView && ouLevel === 0}
      >
        <HeaderCell>
          {getColHeaderWithTooltip(t('fsa.organization.cols.disk'))}
        </HeaderCell>
        <Cell dataKey="diskUsage" style={{ padding: '4px 4px 4px 0px' }}>
          {(rowData) => <InstanceCol rowData={rowData} name="diskUsage" />}
        </Cell>
      </Column>
    </>
  );
};

interface IColProps {
  rowData: Record<string, any>;
  name: string;
}
interface IRowProps {
  rowData: Record<string, any>;
}
const InstanceCol = ({ rowData, name }: IColProps) => {
  const [displayIns, setDisplayIns] = useState<Record<string, any>>({});
  const instances = useAppSelector(selectInstances);
  const currentGroups = useAppSelector(selectCurrentGroup);

  const initData = () => {
    if (currentGroups?.id && rowData?.account_id) {
      const inst = instances?.[currentGroups.id]?.[rowData?.account_id];
      if (inst) {
        setDisplayIns({ ...inst });
      }
    }
  };

  useEffect(() => {
    initData();
  }, [rowData?.account_id]);
  useEffect(() => {
    initData();
  }, [instances, currentGroups]);

  const getContent = (name: string) => {
    if (name === 'diskUsage' && displayIns && 'diskSize' in displayIns) {
      let content = (
        <Stack
          alignItems="center"
          style={{ display: 'inline-flex', width: '100%' }}
        >
          <Stack.Item style={{ width: '150px' }}>
            <Progress.Line
              percent={formatPercent(displayIns?.disk?.percent)}
              status="active"
              strokeWidth={15}
              style={{ borderRadius: '0' }}
            />
          </Stack.Item>
          <div className={rowData?.account_id ? 'account-row' : ''}>
            {`(${displayIns?.diskSize || 0}GB)`}
          </div>
        </Stack>
      );
      return (
        <div className={rowData?.account_id ? 'account-row' : ''}>
          {content}
        </div>
      );
    }
    return displayIns?.[name];
  };

  return rowData?.account_id ? (
    <span className="account-row">{getContent(name)}</span>
  ) : (
    <span></span>
  );
};

const getInfoIcons = (rowData: Record<string, any>) => {
  const [isExpired, setIsExpired] = useState<boolean>(false);
  const [isExpiring, setIsExpiring] = useState<boolean>(false);
  const [hasEntitleAlert, setHasEntitleAlert] = useState<boolean>(false);
  const instances = useAppSelector(selectInstances);
  const instancesExtraInfo = useAppSelector(selectInstancesExtraInfo);
  const currentGroups = useAppSelector(selectCurrentGroup);
  const [upgradeInfo, setUpgradeInfo] = useState<Record<string, any>>({});
  const [isDisabled, setIsDisabled] = useState<boolean>(false);

  useEffect(() => {
    const inst = instances?.[currentGroups.id]?.[rowData?.account_id];
    const extraInfo =
      instancesExtraInfo?.[currentGroups.id]?.[rowData?.account_id];
    if (inst) {
      const license = inst.detail?.license || {};
      const isExpiring = license.isExpiring;
      const isExpired = license.isExpired;
      const hasEntitleAlert = license.hasEntitleAlert;
      setIsExpired(isExpired);
      setIsExpiring(isExpiring);
      setHasEntitleAlert(hasEntitleAlert);
      setIsDisabled(inst?.is_expired);
    }
    if (extraInfo && extraInfo?.firmware_download) {
      setUpgradeInfo({
        ...extraInfo?.firmware_download,
        state: 'NEED_UPGRADE',
        need_force_upgrade: extraInfo?.need_force_upgrade
      });
    }
  }, [instances, currentGroups, instancesExtraInfo]);

  return (
    <Stack spacing={6} style={{ display: 'inline-flex', marginLeft: '6px' }}>
      {hasEntitleAlert && (
        <Whisper
          placement="top"
          controlId="control-id-hover"
          trigger="hover"
          speaker={<Tooltip>{t('common.orgFilter.hasAlarm')}</Tooltip>}
        >
          <i className="icon-fontello-alert" style={{ color: '#ffb300' }}></i>
        </Whisper>
      )}
      {isExpiring && !isExpired && (
        <Whisper
          placement="top"
          controlId="control-id-hover"
          trigger="hover"
          speaker={<Tooltip>{t('common.orgFilter.licenseExpiring')}</Tooltip>}
        >
          <i
            className="icon-fontello-expiring"
            style={{ color: '#ffb300' }}
          ></i>
        </Whisper>
      )}
      {isExpired && (
        <Whisper
          placement="top"
          controlId="control-id-hover"
          trigger="hover"
          speaker={<Tooltip>{t('common.orgFilter.licenseExpired')}</Tooltip>}
        >
          <i className="icon-fontello-expired" style={{ color: '#f44336' }}></i>
        </Whisper>
      )}
      {!isDisabled &&
        (upgradeInfo?.state === 'NEED_UPGRADE' ||
          upgradeInfo?.need_force_upgrade) && (
          <Whisper
            placement="top"
            controlId="control-id-hover"
            trigger="hover"
            speaker={
              <Tooltip>
                {upgradeInfo?.need_force_upgrade
                  ? t(`common.osUpgradeDialog.forceUpgrade`)
                  : t('common.osUpgradeDialog.needUpgrade')}
              </Tooltip>
            }
          >
            <i
              className="icon-fortinetone-upgradeIcon"
              style={{
                color: upgradeInfo?.need_force_upgrade ? '#f44336' : '#ffb300',
                lineHeight: 'revert'
              }}
            ></i>
          </Whisper>
        )}
    </Stack>
  );
};
